import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { WbHeaderButton, WbIcon } from "@workbench/react";
import { useGetNotificationSummaryQuery } from "../../store/contractsApi";
import type { NotificationItem } from "../../types/notifications";
import { requestAppNavigation } from "../../utils/navigationGuard";
import "./NotificationBell.css";

const HELPER_TEXT: Record<string, string> = {
  OPEN_UNMATCHED_LEGAL_ENTITIES: "Source legal entities waiting for mapping.",
  USAGE_ACTIVE_WITHOUT_CONTRACT:
    "Entities are in use but contract details have not been added.",
  CONTRACT_EXISTS_USAGE_INACTIVE:
    "Contract details exist for entities currently marked not in use.",
};

function formatBadgeCount(count: number): string {
  if (count > 99) return "99+";
  return String(count);
}

function NotificationBell(): React.ReactElement {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isError } = useGetNotificationSummaryQuery();

  const close = useCallback(() => setOpen(false), []);

  // Close on click outside — covers both the button wrapper and the portal panel
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      const path = e.composedPath();
      const insideButton =
        buttonRef.current && path.includes(buttonRef.current);
      const insidePanel = panelRef.current && path.includes(panelRef.current);
      if (!insideButton && !insidePanel) close();
    }
    document.addEventListener("mousedown", handleMouseDown, { passive: true });
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, close]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPanelPos({
          top: rect.bottom + window.scrollY,
          right: window.innerWidth - rect.right,
        });
      }
      return !prev;
    });
  }, []);

  const handleItemClick = useCallback(
    (item: NotificationItem) => {
      close();
      const params = item.target.params
        ? "?" + new URLSearchParams(item.target.params).toString()
        : "";
      const href = item.target.page + params;
      if (requestAppNavigation(href)) {
        navigate(href);
      }
    },
    [close, navigate],
  );

  const total = data?.total ?? 0;
  const activeCategoryCount =
    data?.items.filter((item) => item.count > 0).length ?? 0;
  const showBadge = activeCategoryCount > 0;
  const ariaLabel = showBadge
    ? `Notifications, ${activeCategoryCount} categor${activeCategoryCount !== 1 ? "ies" : "y"} need${activeCategoryCount === 1 ? "s" : ""} attention`
    : "Notifications";

  const panel = open
    ? ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="notification-panel"
          role="dialog"
          aria-label="Notifications"
          data-testid="notification-panel"
          style={{ top: panelPos.top, right: panelPos.right }}
        >
          <div className="notification-panel-header">
            <h2 className="notification-panel-title">Notifications</h2>
          </div>

          {isError && (
            <div className="notification-panel-error" role="alert">
              Could not load notifications.
            </div>
          )}

          {!isError && total === 0 && (
            <div className="notification-panel-empty">No notifications</div>
          )}

          {!isError &&
            data?.items.map((item) => (
              <button
                key={item.key}
                type="button"
                className="notification-item"
                onClick={() => handleItemClick(item)}
                data-testid={`notification-item-${item.key}`}
              >
                <span
                  className={`notification-item-count notification-item-count--${item.severity}`}
                  aria-hidden="true"
                >
                  {item.count}
                </span>
                <span className="notification-item-text">
                  <span className="notification-item-label">{item.label}</span>
                  <span className="notification-item-helper">
                    {HELPER_TEXT[item.key]}
                  </span>
                </span>
              </button>
            ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="notification-bell-wrapper" ref={buttonRef}>
      <WbHeaderButton
        label={ariaLabel}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="notification-bell-button"
      >
        <span className="notification-bell-icon-wrap">
          <WbIcon name="bds/notification/24" />
          {showBadge && (
            <span
              className="notification-bell-badge"
              aria-hidden="true"
              data-testid="notification-badge"
            >
              {formatBadgeCount(activeCategoryCount)}
            </span>
          )}
        </span>
      </WbHeaderButton>

      {panel}
    </div>
  );
}

export default NotificationBell;
